import React, { useState, useEffect } from 'react';
import { Paper } from '@material-ui/core';
import { MenuItem, MenuList, ClickAwayListener, Popper, Grow, Divider, ListItemIcon, ListItemText } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import NoteAddOutlined from '@material-ui/icons/NoteAddOutlined';
import { ArrowUpward, ArrowDownward, ArrowBack, ArrowForward } from '@material-ui/icons';
import DeleteIcon from '@material-ui/icons/Delete';
import SubdirectoryArrowRightIcon from '@material-ui/icons/SubdirectoryArrowRight';

import { SheetDb } from '../redux/sheetDbSlice';

export type TreeMenuAnchorType = { open: boolean, anchorPosition: { left: number; top: number; }; data?: { id: string; sheet: SheetDb; }; }
export type TreeMenuFunctionsType = {
  add: (isNoParent?: boolean) => void;
  addChild: () => void;
  copy: () => void;
  copyRecursive: () => void;
  paste?: () => void;
  duplicate: () => void;
  duplicateRecursive: () => void;
  up?: () => void;
  down?: () => void;
  left?: () => void;
  right?: () => void;
  delete: () => void;
}
type HandlingKey = keyof TreeMenuFunctionsType

type MenuItemProps = { key: HandlingKey; text: string; icon: unknown; }
type SubMenuProps = {
  id: string;
  anchorEl?: EventTarget & HTMLElement;
  onClose: () => void;
  handlingExe: (key: HandlingKey) => void;
  menuItems: MenuItemProps[];
}
const SubMenu: React.FC<SubMenuProps> = ({ id, anchorEl, onClose, handlingExe, menuItems }) => {
  return (
  <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} role={undefined} transition disablePortal
    style={{zIndex: 999}} placement="right"
  >
    { ({ TransitionProps, placement }) => (
      <Grow
        { ...TransitionProps }
        style={{ transformOrigin: placement === 'right' ? 'left center' : 'right center' }}
      >
        <Paper>
          <ClickAwayListener onClickAway={onClose}>
            <MenuList id={id}>
              { menuItems.map(item =>
                <MenuItem key={item.key} onClick={() => handlingExe(item.key)}>
                  <ListItemIcon>{ item.icon }</ListItemIcon>
                  <ListItemText>{ item.text }</ListItemText>
                </MenuItem>
              )}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Grow>
    ) }
  </Popper>
  );
}

type TreeMenuProps = {
  menuAnchor?: TreeMenuAnchorType;
  onClose: () => void;
  functions: TreeMenuFunctionsType;
}
const TreeMenu: React.FC<TreeMenuProps> = ({ menuAnchor, onClose, functions }) => {
  const [enable, setEnable] = useState(false);
  const [showCopy, setShowCopy] = useState<EventTarget & HTMLElement>();
  const [showDuplicate, setShowDuplicate] = useState<EventTarget & HTMLElement>();
  const copyMenuId = 'menu-list-grow-copy';
  const duplicateMenuId = 'menu-list-grow-duplicate';

  const closeMenu = () => {
    if (menuAnchor?.data) subMenuClose();
    onClose();
  }
  const handlingExe = (key: HandlingKey) => {
    if (!menuAnchor) return;

    switch (key) {
      case 'add': functions.add(!Boolean(menuAnchor.data)); break;
      case 'addChild': functions.addChild(); break;
      case 'copy': functions.copy(); break;
      case 'copyRecursive': functions.copyRecursive(); break;
      case 'paste': functions.paste && functions.paste(); break;
      case 'duplicate': functions.duplicate(); break;
      case 'duplicateRecursive': functions.duplicateRecursive(); break;
      case 'up': functions.up && functions.up(); break;
      case 'down': functions.down && functions.down(); break;
      case 'left': functions.left && functions.left(); break;
      case 'right': functions.right && functions.right(); break;
      case 'delete': functions.delete(); break;
    }
    closeMenu();
  }
  const toggleEnable = () => {
    setEnable(false);
    if (menuAnchor !== undefined) setTimeout(() => setEnable(true), 280)
  }
  const copyMenuOpen = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    duplicateMenuClose();
    setShowCopy(ev.currentTarget);
  }
  const copyMenuClose = () => {
    setShowCopy(undefined);
  }
  const duplicateMenuOpen = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    copyMenuClose();
    setShowDuplicate(ev.currentTarget);
  }
  const duplicateMenuClose = () => {
    setShowDuplicate(undefined);
  }
  const subMenuClose = () => {
    copyMenuClose();
    duplicateMenuClose();
  }

  useEffect(() => {
    toggleEnable();
    if (menuAnchor?.data) subMenuClose();
  }, [menuAnchor])

  return (
    <Popper open={Boolean(menuAnchor?.open)} role={undefined} transition
      style={{
        zIndex: 998,
        pointerEvents: enable ? 'auto' : 'none',
        left: menuAnchor?.anchorPosition.left,
        top: menuAnchor?.anchorPosition.top
      }}
    >
      { ({ TransitionProps, placement }) => (
        <Grow
          { ...TransitionProps }
          style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
        >
        <Paper>
          <ClickAwayListener onClickAway={closeMenu}>
            <MenuList>
              <MenuItem key="add" onClick={() => handlingExe('add')} onMouseOver={subMenuClose}>
                <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                <ListItemText>同じ階層に新しいシートを追加する</ListItemText>
              </MenuItem>
              <MenuItem key="addChild" onClick={() => handlingExe('addChild')} onMouseOver={subMenuClose}>
                <ListItemIcon><SubdirectoryArrowRightIcon fontSize="small" /><AddIcon fontSize="small" /></ListItemIcon>
                <ListItemText>下の階層に新しいシートを追加する</ListItemText>
              </MenuItem>
              { menuAnchor?.data ?
                <div>
                  <Divider />
                  <MenuItem key="copy" onClick={() => handlingExe('copy')}
                    onMouseOver={ev => copyMenuOpen(ev)}
                    aria-controls={copyMenuId}
                    aria-haspopup="true"
                  >
                    <ListItemIcon><FileCopyOutlined fontSize="small" /></ListItemIcon>
                    <ListItemText>このシートをコピーする</ListItemText>
                    <ListItemIcon><ArrowForwardIosIcon fontSize="small" style={{ position: 'relative', left: 24 }} /></ListItemIcon>
                  </MenuItem>
                  <SubMenu id={copyMenuId}
                    anchorEl={showCopy}
                    onClose={copyMenuClose}
                    handlingExe={handlingExe}
                    menuItems={[
                      { key: 'copy', text: 'このシートをコピーする', icon: <FileCopyOutlined fontSize="small" /> },
                      { key: 'copyRecursive', text: '階層ごとコピーする', icon: <FileCopyOutlined fontSize="small" /> }
                    ]}
                  />
                  <MenuItem key="duplicate" onClick={() => handlingExe('duplicate')}
                    onMouseOver={ev => duplicateMenuOpen(ev)}
                    aria-controls={duplicateMenuId}
                    aria-haspopup="true"
                  >
                    <ListItemIcon><FileCopyIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>このシートを複写する</ListItemText>
                    <ListItemIcon><ArrowForwardIosIcon fontSize="small" style={{ position: 'relative', left: 24 }} /></ListItemIcon>
                  </MenuItem>
                  <SubMenu id={duplicateMenuId}
                    anchorEl={showDuplicate}
                    onClose={duplicateMenuClose}
                    handlingExe={handlingExe}
                    menuItems={[
                      { key: 'duplicate', text: 'このシートを複写する', icon: <FileCopyIcon fontSize="small" /> },
                      { key: 'duplicateRecursive', text: '階層ごと複写する', icon: <FileCopyIcon fontSize="small" /> }
                    ]}
                  />
                  <Divider />
                  <MenuItem key="up" onClick={() => handlingExe('up')} onMouseOver={subMenuClose} disabled={!functions.up}>
                    <ListItemIcon><ArrowUpward fontSize="small" /></ListItemIcon>
                    <ListItemText>上に移動する</ListItemText>
                  </MenuItem>
                  <MenuItem key="down" onClick={() => handlingExe('down')} onMouseOver={subMenuClose} disabled={!functions.down}>
                    <ListItemIcon><ArrowDownward fontSize="small" /></ListItemIcon>
                    <ListItemText>下に移動する</ListItemText>
                  </MenuItem>
                  <MenuItem key="left" onClick={() => handlingExe('left')} onMouseOver={subMenuClose} disabled={!functions.left}>
                    <ListItemIcon><ArrowBack fontSize="small" /></ListItemIcon>
                    <ListItemText>左に移動する</ListItemText>
                  </MenuItem>
                  <MenuItem key="right" onClick={() => handlingExe('right')} onMouseOver={subMenuClose} disabled={!functions.right}>
                    <ListItemIcon><ArrowForward fontSize="small" /></ListItemIcon>
                    <ListItemText>右に移動する</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem key="delete" onClick={() => handlingExe('delete')} onMouseOver={subMenuClose}>
                    <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>このシートを削除する</ListItemText>
                  </MenuItem>
                </div>
                :
                <MenuItem key="paste" onClick={() => handlingExe('paste')} disabled={!functions.paste}>
                  <ListItemIcon><NoteAddOutlined fontSize="small" /></ListItemIcon>
                  <ListItemText>シートを貼り付ける</ListItemText>
                </MenuItem>
              }
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Grow>
    )}
    </Popper>
  );
}

export default TreeMenu;
